import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { Cascader, Select } from 'antd'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { DatePicker } from '@/components/organisms/customer/sales-manager/date-picker'
import { PaymentSettingType, SalesType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { ReservationType } from '@/models/reservation'
import { getRoomCascadeOptionsByReservations } from '@/utils/reservations'
import { initializeSelectBoxStyle } from '@/constants/form'
import '@/components/molecules/customer/sales-manager/usage-detail/ant-select.css'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { SubjectSelect } from './subject-select'
import { SalesSubjectMasterType } from '@/models/sales-manager/sales'
import { cascaderNameSearch } from '@/libs/cascader-name-search'

const QUANTITY_INPUT_PATTERN = /^-?\d*$/

type NotConfirmedUsageDetailProps = {
  index: number
  usageDetail: SalesType
  salesSubjects: SalesSubjectMasterType[]
  salesPayments: PaymentSettingType[]
  confirmedDay: string
  allowClosedDates?: boolean
  isEditMode: boolean
  advanceCompany: any
  changeNotConfirmedDate: (date, index) => void
  changeNotConfirmedState: (e, index) => void
  changeNotConfirmedQuantity: (index: number, quantity: number) => void
  deleteUsageDetail: (index) => void
  setIsEditMode: (boolean) => void
  accountsReceivableSettings: AccountsReceivableSettingType[]
  reservations?: ReservationType[]
  reservation?: ReservationType
  currentTabReservationId?: string
}

export const NotConfirmedUsageDetail: React.FC<NotConfirmedUsageDetailProps> = ({
  index,
  usageDetail,
  salesSubjects,
  salesPayments,
  confirmedDay,
  allowClosedDates = false,
  isEditMode,
  advanceCompany,
  changeNotConfirmedDate,
  changeNotConfirmedState,
  changeNotConfirmedQuantity,
  deleteUsageDetail,
  setIsEditMode,
  accountsReceivableSettings,
  reservations,
  reservation,
  currentTabReservationId,
}) => {
  const { t } = useTranslation()
  const quantity = usageDetail.quantity
  const [quantityDraft, setQuantityDraft] = useState(() => String(quantity))

  useEffect(() => {
    if (!/^-?\d+$/.test(quantityDraft)) return
    if (Number(quantityDraft) === quantity) return
    setQuantityDraft(String(quantity))
  }, [quantity])

  const commitQuantityDraft = () => {
    const num = Number(quantityDraft) || 0
    changeNotConfirmedQuantity(index, num)
    setQuantityDraft(String(num))
  }

  const newAdvanceCompany = advanceCompany?.paymentCompanies?.map(item => ({
    value: item?.id,
    label: item?.name,
    disabled: !item.currentBalance,
  }))
  const salesPaymentOptions =
    salesPayments?.map(item => {
      if (Object.keys(advanceCompany?.paymentMethod || {}).length && item?.id === advanceCompany?.paymentMethod?.id) {
        return { value: item?.id, label: item?.name, children: newAdvanceCompany }
      } else {
        return { value: item?.id, label: item?.name }
      }
    }) || []

  const roomsOptions = getRoomCascadeOptionsByReservations({ reservation, reservations })

  const displayRender = label => label[label.length - 1]

  useEffect(() => {
    //If there is no initial value for usageDetail, substitute the initial value
    if (!usageDetail.reservationId && currentTabReservationId) {
      usageDetail.reservationId = currentTabReservationId
    }
    if (!usageDetail.salesAccountsReceivableMasterId) {
      usageDetail.salesAccountsReceivableMasterId = ''
    }
  }, [salesSubjects])

  return (
    <tr>
      <td>
        <div onClick={() => setIsEditMode(true)}>
          <DatePicker
            date={usageDetail.salesDate ? moment(usageDetail.salesDate) : null}
            setDate={date => changeNotConfirmedDate(date, index)}
            confirmedDay={confirmedDay}
            allowClosedDates={allowClosedDates}
            inputId={`usage-detail-date-${index}`}
          />
        </div>
      </td>
      <td>
        <SubjectSelect
          onClick={() => setIsEditMode(true)}
          onChange={value => {
            changeNotConfirmedState({ target: { value: value.subjectMaster.id, name: 'salesSubjectId' } }, index)
            changeNotConfirmedState({ target: { value: value.subSubject?.id, name: 'salesSubSubjectId' } }, index)
          }}
          defaultValue={usageDetail}
          salesSubjectMasters={salesSubjects}
          isEditMode={isEditMode}
        />
      </td>
      <td>
        <Select
          onChange={value => {
            setIsEditMode(true)
            const reservationId = roomsOptions?.find(room => room.value === value)?.reservationId
            changeNotConfirmedState({ target: { value: reservationId, name: 'reservationId' } }, index)
            changeNotConfirmedState({ target: { value: value, name: 'guestRoomAssignId' } }, index)
          }}
          value={usageDetail?.guestRoomAssignId || undefined}
          suffixIcon={isEditMode ? <img src={require('@/static/images/arrow_down_yellow.svg')} /> : <></>}
          placeholder={t('Please select')}
        >
          {roomsOptions.map(room => (
            <Select.Option key={room.value} value={room.value}>
              {room.label}
            </Select.Option>
          ))}
        </Select>
      </td>
      <td>
        <input
          name="salesSubjectPrice"
          type="text"
          css={unitPriceStyle}
          onClick={() => setIsEditMode(true)}
          value={usageDetail.salesSubjectPrice}
          onChange={e => changeNotConfirmedState(e, index)}
          placeholder="0000"
          className="unitPriceStyle"
        />
      </td>
      <td>
        <input
          name="quantity"
          type="text"
          css={smallSelectBoxStyle}
          onClick={() => setIsEditMode(true)}
          value={quantityDraft}
          onChange={e => {
            const value = e.target.value
            if (!QUANTITY_INPUT_PATTERN.test(value)) return
            setQuantityDraft(value)
            if (/^-?\d+$/.test(value)) {
              changeNotConfirmedQuantity(index, Number(value))
            }
          }}
          onBlur={commitQuantityDraft}
          placeholder="0"
          className="quantityStyle"
        />
      </td>
      <td>{Number(usageDetail.salesPrice).toLocaleString()}</td>
      <td>
        <Cascader
          options={salesPaymentOptions}
          expandTrigger="hover"
          className={isEditMode ? 'cascader-customize' : 'cascader-customize not-edit-mode-cascader'}
          value={usageDetail.companyId ? [usageDetail.salesPaymentId, usageDetail.companyId] : [usageDetail.salesPaymentId]}
          displayRender={displayRender}
          popupClassName="popup-cascader-customize"
          showSearch={cascaderNameSearch}
          onChange={async value => {
            setIsEditMode(true)
            const eventChangeSalesPaymentId = {
              target: { value: value[0], name: 'salesPaymentId' },
            }

            const eventChangeCompanyId = {
              target: { value: value[1] ? value[1] : '', name: 'companyId' },
            }
            await changeNotConfirmedState(eventChangeSalesPaymentId, index)
            changeNotConfirmedState(eventChangeCompanyId, index)
          }}
          allowClear={false}
          suffixIcon={<span></span>}
        />
      </td>
      <td>
        <select
          name="isPaid"
          css={isEditMode ? editModeMiddleSelectBoxStyle : middleSelectBoxStyle}
          onClick={() => setIsEditMode(true)}
          className={!usageDetail.isPaid ? 'unpaidStyle' : 'paidStyle'}
          defaultValue={!usageDetail.isPaid ? '0' : '1'}
          onChange={e => changeNotConfirmedState(e, index)}
        >
          <option selected={!usageDetail.isPaid} value="0">
            {t('Unpaid')}
          </option>
          <option selected={!!usageDetail.isPaid} value="1">
            {t('Paid')}
          </option>
        </select>
      </td>
      <td>
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={true}
          suffixIcon={isEditMode ? <img src={require('@/static/images/arrow_down_yellow.svg')} /> : <></>}
          onChange={value => {
            setIsEditMode(true)
            changeNotConfirmedState(
              {
                target: { value, name: 'salesAccountsReceivableMasterId' },
              },
              index,
            )
          }}
          value={usageDetail.salesAccountsReceivableMasterId}
        >
          <Select.Option value="">-</Select.Option>
          {accountsReceivableSettings.map((accountsReceivable, index) => (
            <Select.Option key={index} value={accountsReceivable.id}>
              {accountsReceivable.name}
            </Select.Option>
          ))}
          {usageDetail.salesAccountsReceivableMasterId !== '' &&
            !accountsReceivableSettings.some(
              accountsReceivable => accountsReceivable.id === usageDetail.salesAccountsReceivableMasterId,
            ) && (
              <Select.Option value={usageDetail.salesAccountsReceivableMasterId}>
                {usageDetail.salesAccountsReceivableName}
              </Select.Option>
            )}
        </Select>
      </td>
      <td>{isEditMode && <DeleteIcon onClick={() => deleteUsageDetail(index)} />}</td>
    </tr>
  )
}

const selectBoxStyle = css(initializeSelectBoxStyle, {
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 14,
  marginRight: 10,
})

const smallSelectBoxStyle = css(selectBoxStyle, {
  width: '100%',
  backgroundPosition: '80% center',
})

const middleInputStyle = css(selectBoxStyle, {
  backgroundImage: 'none',
  '::placeholder': {
    color: '#ccc',
  },
})

export const unitPriceStyle = css(middleInputStyle, {
  marginLeft: 12,
})

const middleSelectBoxStyle = css(selectBoxStyle, {
  backgroundPosition: 'calc(100% - 8px) center',
})

export const xlargeSelectBoxStyle = css(selectBoxStyle, {
  backgroundPosition: 'calc(100% - 8px) center',
})

// 編集モードのセレクトボックス
const editModoSelectBoxStyle = css(selectBoxStyle, {
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
})

const editModeMiddleSelectBoxStyle = css(middleSelectBoxStyle, editModoSelectBoxStyle)
